import { Autocomplete, Stack, TextField, Typography, Popper } from "@mui/material";
import { useEffect, useState } from "react";
import { IOSSwitch } from "../../shared/Components/iosSwitch";
import { useTBContext } from "../Contexts/useTBStates";
import { utcToLocalDateString, formatDateForDisplay } from "@moment-golf/shared";

interface RoundItemDisplayProps {
    orient: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    label: string;
    keyName: string;
    labelField?: string;
    handleUpdate: (roundIndex: number, keyName: string, keyValue: any, keyIDName?: string, keyIDValue?: any) => void;
    optionsKeyName?: string;
    freeSolo?: boolean;
    hideLabel?: boolean;
    flipped?: boolean;
    extraFactor?: number | 1;
    locked?: boolean;
}


export const RoundItemDisplay = ({ 
        orient, label, keyName, labelField, handleUpdate, optionsKeyName, freeSolo, hideLabel, flipped, extraFactor, locked,
}: RoundItemDisplayProps) => {

    const { roundValue, factor, appTheme, editMode, viewEvent, forceUpdate, optionsLoaded } = useTBContext();

    const round = viewEvent.rounds[roundValue - 1];
    const roundIndex = roundValue - 1;
    const thisOptions = optionsLoaded.find((o: any) => o.keyName === optionsKeyName)?.options || [];

    function formatEditValue() {

        let newValue: any;

        if (keyName.toLowerCase().includes("date")) {
            newValue = round[keyName] ? utcToLocalDateString(round[keyName]) : null;
        }
        else {
            newValue = round[keyName];
        }

        return newValue;
    }

    function formatDisplayValue(value?: any) {

        let newValue = value || round[keyName];


        if (keyName.toLowerCase().includes("date")) {
            newValue = newValue ? formatDateForDisplay(newValue) : null;
        }
        else if (keyName.toLowerCase().includes("time") && typeof newValue === 'string') {
            const timeParts = newValue.split(':');
            const hours = parseInt(timeParts[0], 10);
            const minutes = timeParts[1];
            const period = hours >= 12 ? 'PM' : 'AM';
            newValue = `${hours % 12 || 12}:${minutes} ${period}`;
        }
        else {
            newValue = newValue;
        }

        return newValue;
    }

    function getDataType() {
        const value = round[keyName];

        if (typeof value === 'number') {
            return 'number';
        } else if (typeof value === 'string' && value.includes(':') && !value.includes('T') && keyName !== 'regPerTeeTime') {
            return 'time';
        } else if (value instanceof Date || !isNaN(Date.parse(value))) {
            return 'date';
        } else if (typeof value === 'boolean') {
            return 'boolean';
        }
        
        return 'string';
    }

    const [blockValue, setBlockValue] = useState<any>(formatEditValue());
    const [displayValue, setDisplayValue] = useState<any>(formatDisplayValue());
    const [dataType, setDataType] = useState<string>(getDataType());

    useEffect(() => {
        setBlockValue(formatEditValue());
        setDisplayValue(formatDisplayValue());
    }, [forceUpdate, roundValue, round[keyName]]);
    
    return (
        <>
            {editMode && !locked ? (
                <Stack
                    direction={orient}
                    spacing={1}
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        justifyContent: 'space-between',
                        alignItems: orient === 'row' || orient === 'row-reverse' ? 'center' : 'flex-start',
                    }}
                >
                    {!hideLabel &&
                        <Typography
                            flex={2}
                            width={orient === 'row' ? 'auto' : '100%'}
                            textAlign="left"
                            sx={{
                                fontSize: 14 * factor * (extraFactor || 1),
                                fontWeight: 500,
                                lineHeight: 1.4,
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                                flexShrink: 1,
                            }}
                        >
                            {label}:
                        </Typography>}
                    {thisOptions && thisOptions.length > 0 && labelField && (
                        <Autocomplete
                            fullWidth
                            freeSolo={freeSolo || false}
                            options={thisOptions}
                            getOptionLabel={(option) => option[labelField]}
                            value={thisOptions.find((o: any) => o[labelField] === displayValue) || null}
                            onChange={(e, newValue) => {
                                setBlockValue(newValue?.[labelField] || '')
                                setDisplayValue(newValue?.[labelField] || '')
                                handleUpdate(roundIndex, keyName, newValue[labelField] || '');
                            }}
                            sx={{
                                flex: 3,
                                minWidth: 0,
                                maxWidth: '100%',
                                padding: 0,
                                '& .MuiAutocomplete-inputRoot': {
                                    padding: 0,
                                }
                            }}
                            renderInput={(params) => <TextField
                                {...params}
                                variant="outlined"
                                sx={{
                                    padding: 0,
                                    "& .MuiInputBase-input": { fontSize: 14 * factor * (extraFactor || 1), height: 14 * factor * (extraFactor || 1), padding: 1 },
                                    '& .MuiOutlinedInput-root': { padding: '0px 0px' },
                                    '& input': { textAlign: 'left', lineHeight: 'normal' },
                                    flex: 3,
                                    width: '100%',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    inputMode: "text",
                                    autoComplete: "off",
                                    sx: {
                                        fontSize: 14 * (factor ? factor : 1),
                                    },
                                }}
                                InputLabelProps={{
                                    sx: { fontSize: 16 * (factor ? factor : 1) * (extraFactor || 1), lineHeight: 'normal', padding: '0px 10px', backgroundColor: 'whitesmoke', marginTop: '0px' },
                                    shrink: true
                                }}
                            />}
                            PopperComponent={(props) => (
                                <Popper {...props} autoFocus sx={{
                                    zIndex: 1300,
                                    '& .MuiAutocomplete-listbox': {
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        bgcolor: 'whitesmoke',
                                    },
                                    '& .MuiAutocomplete-option': {
                                        padding: '5px 10px',
                                        '&:hover': {
                                            backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                            borderRadius: 3
                                        },
                                        fontSize: 14 * (factor ? factor : 1),
                                    },
                                    '& .Mui-focusVisible': {
                                        backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                        borderRadius: 3,
                                    },
                                }} />
                            )}
                        />
                    )}
                    {(!thisOptions || thisOptions.length === 0) && dataType !== 'boolean' && (
                        <TextField
                            value={dataType === 'time' ? blockValue : displayValue}
                            type={dataType}
                            onChange={(e) => {
                                const newValue = e.target.value;

                                const formattedValue = dataType === 'number' ? Number(newValue) : newValue;
                                setBlockValue(formattedValue);
                                setDisplayValue(formatDisplayValue(formattedValue));
                                handleUpdate(roundIndex, keyName, formattedValue);
                            }}
                            sx={{
                                padding: 0,
                                "& .MuiInputBase-input": { fontSize: 14 * factor * (extraFactor || 1), height: 14 * factor * (extraFactor || 1), padding: 1 },
                                '& .MuiOutlinedInput-root': { padding: '0px 0px' },
                                '& input': { textAlign: 'left', lineHeight: 'normal' },
                                flex: 3,
                                width: '100%',
                                minWidth: 0,
                                maxWidth: '100%',
                            }}
                            InputProps={{
                                sx: {
                                    fontSize: 14 * factor * (extraFactor || 1),
                                },
                            }}
                        />)}
                    {dataType === 'boolean' && (
                        <Stack direction='row' flex={3} spacing={1} justifyContent='flex-start' alignItems='center' sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14 * factor * (extraFactor || 1), fontWeight: 500 }}>No</Typography>
                            <IOSSwitch
                                checked={displayValue}
                                onChange={(e) => {
                                    setBlockValue(e.target.checked);
                                    handleUpdate(roundIndex, keyName, e.target.checked);
                                }}
                            />
                            <Typography sx={{ fontSize: 14 * factor * (extraFactor || 1), fontWeight: 500 }}>Yes</Typography>
                        </Stack>
                    )}
                </Stack>
            ) :
                (
                    <Stack
                        direction={orient}
                        spacing={orient === 'row' ? 1 : 0.5}
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            justifyContent: 'flex-start',
                            alignItems: orient === 'row' || orient === 'row-reverse' ? 'center' : 'flex-start',
                        }}
                    >
                        {!hideLabel && <Typography
                            flex={2}
                            width={orient === 'row' ? 'auto' : '100%'}
                            textAlign="left"
                            padding={0}
                            sx={{
                                fontWeight: 500,
                                fontSize: 14 * factor * (extraFactor || 1),
                                lineHeight: 1.4,
                                minHeight: 24 * factor * (extraFactor || 1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                minWidth: 0,
                                flexShrink: 1,
                            }}>
                            {label}:
                        </Typography>}
                        {dataType !== 'boolean' && (
                            <Typography
                                flex={3}
                                width={orient === 'row' ? 'auto' : '100%'}
                                textAlign={flipped ? 'right' : 'left'}
                                padding={0}
                                color={displayValue ? 'inherit' : 'text.disabled'}
                                sx={{
                                    fontSize: 14 * factor * (extraFactor || 1),
                                    lineHeight: 1.4,
                                    minHeight: 24 * factor * (extraFactor || 1),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: flipped ? 'flex-end' : 'flex-start',
                                    minWidth: 0,
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                }}>
                                {displayValue ? displayValue : label}
                            </Typography>
                        )}
                        {dataType === 'boolean' && (
                            <Stack direction='row' flex={3} spacing={1} justifyContent='flex-start' alignItems='center' sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14 * factor * (extraFactor || 1), fontWeight: 500 }}>No</Typography>
                                <IOSSwitch
                                    checked={blockValue}
                                    disabled
                                    onChange={(e) => {
                                        handleUpdate(roundIndex, keyName, e.target.checked);
                                    }}
                                />
                                <Typography sx={{ fontSize: 14 * factor * (extraFactor || 1), fontWeight: 500 }}>Yes</Typography>
                            </Stack>
                        )}
                    </Stack>
                )}
        </>
    )
}
